import { Suspense, lazy, useContext } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import AuthContext from "./context/AuthContext";
import AppShell from "./components/AppShell";
import RequireAuth from "./components/RequireAuth";
import NotFound from "./components/NotFound";
import LoginPage from "./components/LoginPage";
import ReviewList from "./components/ReviewList";
import CreateMemory from "./components/CreateMemory";
import MemoryList from "./components/MemoryList";
import DeckList from "./components/decks/DeckList";
import TaskBoard from "./components/tasks/TaskBoard";
import ProjectList from "./components/tasks/ProjectList";
import TaskArchive from "./components/tasks/TaskArchive";
import ActivityFeed from "./components/tasks/ActivityFeed";
import DiscussionList from "./components/discussions/DiscussionList";
import NewDiscussion from "./components/discussions/NewDiscussion";
import DiscussionThread from "./components/discussions/DiscussionThread";
import SnippetList from "./components/snippets/SnippetList";
import CreateSnippet from "./components/snippets/CreateSnippet";
import EditSnippet from "./components/snippets/EditSnippet";
import SnippetDetail from "./components/snippets/SnippetDetail";
import DomainSearch from "./components/domain-search/DomainSearch";
import DocumentList from "./components/documents/DocumentList";
import AddDocument from "./components/documents/AddDocument";
import DocumentDetail from "./components/documents/DocumentDetail";
import TagReview from "./components/documents/TagReview";
import AgentList from "./components/agents/AgentList";
import MyToken from "./components/agents/MyToken";
import HouseholdPage from "./components/agents/HouseholdPage";
import AgentDetail from "./components/agents/AgentDetail";
import AgentRoutes from "./components/agents/AgentRoutes";
import TrainingLayout from "./components/training/TrainingLayout";
import TrainingToday from "./components/training/TrainingToday";
import TrainingHistory from "./components/training/TrainingHistory";
import TrainingProgress from "./components/training/TrainingProgress";
import TrainingSessionDetail from "./components/training/TrainingSessionDetail";
import TrainingSettings from "./components/training/TrainingSettings";
import InvestingLayout from "./components/investing/InvestingLayout";
import HomeVideosLayout from "./components/homeVideos/HomeVideosLayout";
import InvestingCompany from "./components/investing/InvestingCompany";
import InvestingFund from "./components/investing/InvestingFund";
import InvestingResearch from "./components/investing/InvestingResearch";
import InvestingSettings from "./components/investing/InvestingSettings";
import InvestingTarget from "./components/investing/InvestingTarget";
import InvestingTheses from "./components/investing/InvestingTheses";
import InvestingThesis from "./components/investing/InvestingThesis";
import TriviaHost from "./components/trivia/TriviaHost";
import TriviaHostGame from "./components/trivia/TriviaHostGame";
import TriviaPlay from "./components/trivia/TriviaPlay";
import { DEFAULT_ROUTE } from "./routes/domains";
import { safeReturnPath } from "./lib/returnPath";

// Drawings load on demand — the editor carries Excalidraw (large) — so the
// rest of the app's bundle doesn't grow (projects/drawings D6).
const DrawingGallery = lazy(
  () => import("./components/drawings/DrawingGallery"),
);
const DrawingEditor = lazy(() => {
  // Before Excalidraw's code runs: its fonts come from mono, not a CDN
  // (the editor module sets it too; this makes the order certain).
  window.EXCALIDRAW_ASSET_PATH = "/excalidraw-assets/";
  return import("./components/drawings/DrawingEditor");
});

// The House section loads on demand too: the editor carries the plan view
// and (lazily again, inside it) three.js (projects/yates-planner D1).
const HousePage = lazy(() => import("./components/planner/HousePage"));
const PlannerEditor = lazy(() => import("./components/planner/PlannerEditor"));

// Home videos load on demand: the player, the uploader and the editor are
// a section of their own (projects/home-videos).
const HomeVideosLibrary = lazy(() => import("./components/homeVideos/HomeVideosLibrary"));
const HomeVideoUpload = lazy(() => import("./components/homeVideos/HomeVideoUpload"));
const HomeVideoPage = lazy(() => import("./components/homeVideos/HomeVideoPage"));
const HomeVideoPeople = lazy(() => import("./components/homeVideos/HomeVideoPeople"));
const HomeVideoReels = lazy(() => import("./components/homeVideos/HomeVideoReels"));
const ReelBuilder = lazy(() => import("./components/homeVideos/ReelBuilder"));

function HomeVideosFallback() {
  return (
    <div className="py-12 text-center text-muted-foreground" role="status">
      Opening home videos…
    </div>
  );
}

/** A home videos page inside its Suspense fallback. */
function homeVideos(Page) {
  return (
    <Suspense fallback={<HomeVideosFallback />}>
      <Page />
    </Suspense>
  );
}

function HouseFallback() {
  return (
    <div className="py-12 text-center text-muted-foreground" role="status">
      Opening the house…
    </div>
  );
}

function DrawingsFallback() {
  return (
    <div className="py-12 text-center text-muted-foreground" role="status">
      Opening drawings…
    </div>
  );
}

function RootRedirect() {
  const { isLoggedIn } = useContext(AuthContext);
  return <Navigate to={isLoggedIn ? DEFAULT_ROUTE : "/login"} replace />;
}

function PublicOnly({ children }) {
  const { isLoggedIn } = useContext(AuthContext);
  const location = useLocation();
  if (isLoggedIn) {
    // RequireAuth stashes the deep link it bounced from; land there
    // after login instead of always on the default route — but only if
    // it is a path on this site (the URL was attacker-controlled; C7).
    const from = safeReturnPath(location.state?.from);
    return <Navigate to={from || DEFAULT_ROUTE} replace />;
  }
  return children;
}

PublicOnly.propTypes = {
  children: PropTypes.node.isRequired,
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      {/* A trivia player's phone (projects/trivia): no account, no shell,
          signed in or not. */}
      <Route path="/play" element={<TriviaPlay />} />
      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route path="/trivia" element={<TriviaHost />} />
          <Route path="/trivia/:id" element={<TriviaHostGame />} />
          <Route path="/study/review" element={<ReviewList />} />
          <Route path="/study/create" element={<CreateMemory />} />
          <Route path="/study/all" element={<MemoryList />} />
          <Route path="/study/decks" element={<DeckList />} />
          {/* Spaced repetition moved Knowledge → Study; keep old deep links
              (bookmarks, browser history) working. */}
          <Route
            path="/knowledge/review"
            element={<Navigate to="/study/review" replace />}
          />
          <Route
            path="/knowledge/create"
            element={<Navigate to="/study/create" replace />}
          />
          <Route
            path="/knowledge/all"
            element={<Navigate to="/study/all" replace />}
          />
          <Route path="/knowledge/documents" element={<DocumentList />} />
          <Route path="/knowledge/documents/new" element={<AddDocument />} />
          <Route path="/knowledge/documents/:id" element={<DocumentDetail />} />
          <Route path="/knowledge/tags" element={<TagReview />} />
          {/* Token management moved under Agents; keep the old link. */}
          <Route
            path="/knowledge/auth-token"
            element={<Navigate to="/agents/my-token" replace />}
          />
          <Route path="/tasks/board" element={<TaskBoard />} />
          <Route path="/tasks/projects" element={<ProjectList />} />
          <Route path="/tasks/archive" element={<TaskArchive />} />
          <Route path="/tasks/activity" element={<ActivityFeed />} />
          <Route path="/discussions" element={<DiscussionList />} />
          <Route path="/discussions/new" element={<NewDiscussion />} />
          <Route path="/discussions/:id" element={<DiscussionThread />} />
          <Route path="/snippets" element={<SnippetList />} />
          <Route path="/snippets/new" element={<CreateSnippet />} />
          <Route path="/snippets/:slug" element={<SnippetDetail />} />
          <Route path="/snippets/:slug/edit" element={<EditSnippet />} />
          <Route
            path="/drawings"
            element={
              <Suspense fallback={<DrawingsFallback />}>
                <DrawingGallery />
              </Suspense>
            }
          />
          <Route
            path="/drawings/:id"
            element={
              <Suspense fallback={<DrawingsFallback />}>
                <DrawingEditor />
              </Suspense>
            }
          />
          <Route
            path="/house"
            element={
              <Suspense fallback={<HouseFallback />}>
                <HousePage />
              </Suspense>
            }
          />
          <Route
            path="/house/plans/:planId"
            element={
              <Suspense fallback={<HouseFallback />}>
                <PlannerEditor />
              </Suspense>
            }
          />
          {/* Home videos (projects/home-videos): household-visible; the
              layout scopes its contrast and passes the shell's `me` on;
              the person-only controls (upload, Analyse, confirm) gate
              themselves, failing closed while it's unknown. */}
          <Route element={<HomeVideosLayout />}>
            <Route path="/home-videos" element={homeVideos(HomeVideosLibrary)} />
            <Route path="/home-videos/upload" element={homeVideos(HomeVideoUpload)} />
            <Route path="/home-videos/people" element={homeVideos(HomeVideoPeople)} />
            <Route path="/home-videos/people/:id/reel" element={homeVideos(ReelBuilder)} />
            <Route path="/home-videos/reels" element={homeVideos(HomeVideoReels)} />
            <Route path="/home-videos/:id" element={homeVideos(HomeVideoPage)} />
          </Route>
          <Route path="/domain-search" element={<DomainSearch />} />
          {/* Strength training (projects/strength-training): the layout
              loads the settings and exercise catalog once for every page. */}
          <Route element={<TrainingLayout />}>
            <Route path="/training" element={<TrainingToday />} />
            <Route path="/training/history" element={<TrainingHistory />} />
            <Route
              path="/training/history/:id"
              element={<TrainingSessionDetail />}
            />
            <Route path="/training/progress" element={<TrainingProgress />} />
            <Route path="/training/settings" element={<TrainingSettings />} />
          </Route>
          {/* Investing (projects/investing): the research desk, the
              fund's target and the fund (its scoreboard). The layout
              scopes its contrast and passes the shell's `me` on; Settings
              is a person's and gates itself, and Target shows Approve /
              Decline to a person only (both fail closed). */}
          <Route
            path="/investing"
            element={<Navigate to="/investing/research" replace />}
          />
          <Route element={<InvestingLayout />}>
            <Route path="/investing/research" element={<InvestingResearch />} />
            <Route path="/investing/research/:symbol" element={<InvestingCompany />} />
            <Route path="/investing/theses" element={<InvestingTheses />} />
            <Route path="/investing/theses/:slug" element={<InvestingThesis />} />
            <Route path="/investing/target" element={<InvestingTarget />} />
            <Route path="/investing/fund" element={<InvestingFund />} />
            <Route path="/investing/settings" element={<InvestingSettings />} />
          </Route>
          {/* All three agent pages are for people: the API 403s an agent
              on /agents/ and refuses to hand it its own API token. */}
          <Route element={<AgentRoutes />}>
            <Route path="/agents" element={<AgentList />} />
            <Route path="/agents/household" element={<HouseholdPage />} />
            <Route path="/agents/my-token" element={<MyToken />} />
            <Route path="/agents/:id" element={<AgentDetail />} />
          </Route>
          {/* Unknown paths stay inside the shell (board #81), so the
              header and section nav are still there to go somewhere else.
              Signed out, RequireAuth sends them to log in first. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default AppRoutes;
